import useSWR from 'swr'

import { MutationOptions, useCallMutation } from '@/common/api/client'
import { apiDeleteFetcher, apiPutFetcher } from '@/common/api/fetcher'
import {
  CorrectionActionVars,
  CreateSuggestManualKeywordVars,
  CreateSuggestSynonymVars,
  CreateSuggestWidgetKeyResponse,
  CreateSuggestWidgetKeyVars,
  DeleteSuggestManualKeywordVars,
  DeleteSuggestSynonymVars,
  DeleteSuggestWidgetKeyVars,
  GenerateCorrectionsResponse,
  GenerateCorrectionsVars,
  GetSuggestAnalyticsResponse,
  GetSuggestAnalyticsVars,
  GetSuggestPreviewResponse,
  GetSuggestPreviewVars,
  GetSuggestDeliveryResponse,
  GetSuggestDeliveryVars,
  GetSuggestEmbedTagResponse,
  GetSuggestEmbedTagVars,
  GetSuggestSettingsResponse,
  GetSuggestSettingsVars,
  ListSuggestCorrectionsResponse,
  ListSuggestCorrectionsVars,
  ListSuggestDictBuildsResponse,
  ListSuggestDictBuildsVars,
  ListSuggestManualKeywordsResponse,
  ListSuggestManualKeywordsVars,
  ListSuggestSynonymsResponse,
  ListSuggestSynonymsVars,
  ListSuggestPopularKeywordsResponse,
  ListSuggestPopularKeywordsVars,
  ListSuggestWidgetKeysResponse,
  ListSuggestWidgetKeysVars,
  ListSuggestZeroHitResponse,
  ListSuggestZeroHitVars,
  RebuildSuggestDictResponse,
  RebuildSuggestDictVars,
  SearchSuggestApiPath,
  SuggestCorrection,
  SuggestManualKeyword,
  SuggestSynonym,
  SuggestWidgetKey,
  UpdateSuggestDeliveryResponse,
  UpdateSuggestDeliveryVars,
  UpdateSuggestSettingsResponse,
  UpdateSuggestSettingsVars,
  UpdateSuggestSynonymVars,
  UpdateSuggestWidgetKeyVars
} from '@/common/api/params/searchSuggest'

// --- 設定① ---

export const useSuggestSettingsGetApi = (vars: GetSuggestSettingsVars | undefined) => {
  return useSWR<GetSuggestSettingsResponse, Response, [string, GetSuggestSettingsVars] | undefined>(
    vars ? [SearchSuggestApiPath.GET_SETTINGS, vars] : undefined
  )
}

export const useSuggestSettingsUpdateApi = (
  options?: MutationOptions<UpdateSuggestSettingsResponse>
) => {
  return useCallMutation<UpdateSuggestSettingsResponse, UpdateSuggestSettingsVars>(
    SearchSuggestApiPath.UPDATE_SETTINGS,
    options,
    apiPutFetcher
  )
}

// --- 配信② ---

export const useSuggestDeliveryGetApi = (vars: GetSuggestDeliveryVars | undefined) => {
  return useSWR<GetSuggestDeliveryResponse, Response, [string, GetSuggestDeliveryVars] | undefined>(
    vars ? [SearchSuggestApiPath.GET_DELIVERY, vars] : undefined
  )
}

export const useSuggestDeliveryUpdateApi = (
  options?: MutationOptions<UpdateSuggestDeliveryResponse>
) => {
  return useCallMutation<UpdateSuggestDeliveryResponse, UpdateSuggestDeliveryVars>(
    SearchSuggestApiPath.UPDATE_DELIVERY,
    options,
    apiPutFetcher
  )
}

// --- 配信タグ用 APIキー / 埋め込みタグ ---

export const useSuggestWidgetKeysGetApi = (vars: ListSuggestWidgetKeysVars | undefined) => {
  return useSWR<
    ListSuggestWidgetKeysResponse,
    Response,
    [string, ListSuggestWidgetKeysVars] | undefined
  >(vars ? [SearchSuggestApiPath.LIST_WIDGET_KEYS, vars] : undefined)
}

export const useSuggestWidgetKeyCreateApi = (
  options?: MutationOptions<CreateSuggestWidgetKeyResponse>
) => {
  return useCallMutation<CreateSuggestWidgetKeyResponse, CreateSuggestWidgetKeyVars>(
    SearchSuggestApiPath.CREATE_WIDGET_KEY,
    options
  )
}

export const useSuggestWidgetKeyUpdateApi = (options?: MutationOptions<SuggestWidgetKey>) => {
  return useCallMutation<SuggestWidgetKey, UpdateSuggestWidgetKeyVars>(
    SearchSuggestApiPath.UPDATE_WIDGET_KEY,
    options,
    apiPutFetcher
  )
}

export const useSuggestWidgetKeyDeleteApi = (options?: MutationOptions<{ deleted: boolean }>) => {
  return useCallMutation<{ deleted: boolean }, DeleteSuggestWidgetKeyVars>(
    SearchSuggestApiPath.DELETE_WIDGET_KEY,
    options,
    apiDeleteFetcher
  )
}

export const useSuggestEmbedTagGetApi = (vars: GetSuggestEmbedTagVars | undefined) => {
  return useSWR<GetSuggestEmbedTagResponse, Response, [string, GetSuggestEmbedTagVars] | undefined>(
    vars ? [SearchSuggestApiPath.GET_EMBED_TAG, vars] : undefined
  )
}

// --- 辞書ビルド ---

export const useSuggestDictRebuildApi = (
  options?: MutationOptions<RebuildSuggestDictResponse>
) => {
  return useCallMutation<RebuildSuggestDictResponse, RebuildSuggestDictVars>(
    SearchSuggestApiPath.REBUILD_DICT,
    options
  )
}

export const useSuggestDictBuildsGetApi = (vars: ListSuggestDictBuildsVars | undefined) => {
  return useSWR<
    ListSuggestDictBuildsResponse,
    Response,
    [string, ListSuggestDictBuildsVars] | undefined
  >(vars ? [SearchSuggestApiPath.LIST_DICT_BUILDS, vars] : undefined)
}

// --- シノニム ---

export const useSuggestSynonymsGetApi = (vars: ListSuggestSynonymsVars | undefined) => {
  return useSWR<ListSuggestSynonymsResponse, Response, [string, ListSuggestSynonymsVars] | undefined>(
    vars ? [SearchSuggestApiPath.LIST_SYNONYMS, vars] : undefined
  )
}

export const useSuggestSynonymCreateApi = (options?: MutationOptions<SuggestSynonym>) => {
  return useCallMutation<SuggestSynonym, CreateSuggestSynonymVars>(
    SearchSuggestApiPath.CREATE_SYNONYM,
    options
  )
}

export const useSuggestSynonymUpdateApi = (options?: MutationOptions<SuggestSynonym>) => {
  return useCallMutation<SuggestSynonym, UpdateSuggestSynonymVars>(
    SearchSuggestApiPath.UPDATE_SYNONYM,
    options,
    apiPutFetcher
  )
}

export const useSuggestSynonymDeleteApi = (options?: MutationOptions<{ deleted: boolean }>) => {
  return useCallMutation<{ deleted: boolean }, DeleteSuggestSynonymVars>(
    SearchSuggestApiPath.DELETE_SYNONYM,
    options,
    apiDeleteFetcher
  )
}

// --- 補正辞書 ---

export const useSuggestCorrectionsGetApi = (vars: ListSuggestCorrectionsVars | undefined) => {
  return useSWR<
    ListSuggestCorrectionsResponse,
    Response,
    [string, ListSuggestCorrectionsVars] | undefined
  >(vars ? [SearchSuggestApiPath.LIST_CORRECTIONS, vars] : undefined)
}

export const useSuggestCorrectionApproveApi = (options?: MutationOptions<SuggestCorrection>) => {
  return useCallMutation<SuggestCorrection, CorrectionActionVars>(
    SearchSuggestApiPath.APPROVE_CORRECTION,
    options
  )
}

export const useSuggestCorrectionRejectApi = (options?: MutationOptions<SuggestCorrection>) => {
  return useCallMutation<SuggestCorrection, CorrectionActionVars>(
    SearchSuggestApiPath.REJECT_CORRECTION,
    options
  )
}

export const useSuggestCorrectionGenerateApi = (
  options?: MutationOptions<GenerateCorrectionsResponse>
) => {
  return useCallMutation<GenerateCorrectionsResponse, GenerateCorrectionsVars>(
    SearchSuggestApiPath.GENERATE_CORRECTIONS,
    options
  )
}

// --- 手動キーワード ---

export const useSuggestManualKeywordsGetApi = (
  vars: ListSuggestManualKeywordsVars | undefined
) => {
  return useSWR<
    ListSuggestManualKeywordsResponse,
    Response,
    [string, ListSuggestManualKeywordsVars] | undefined
  >(vars ? [SearchSuggestApiPath.LIST_MANUAL_KEYWORDS, vars] : undefined)
}

export const useSuggestManualKeywordCreateApi = (
  options?: MutationOptions<SuggestManualKeyword>
) => {
  return useCallMutation<SuggestManualKeyword, CreateSuggestManualKeywordVars>(
    SearchSuggestApiPath.CREATE_MANUAL_KEYWORD,
    options
  )
}

export const useSuggestManualKeywordDeleteApi = (
  options?: MutationOptions<{ deleted: boolean }>
) => {
  return useCallMutation<{ deleted: boolean }, DeleteSuggestManualKeywordVars>(
    SearchSuggestApiPath.DELETE_MANUAL_KEYWORD,
    options,
    apiDeleteFetcher
  )
}

// --- 0件キーワード ---

export const useSuggestZeroHitGetApi = (vars: ListSuggestZeroHitVars | undefined) => {
  return useSWR<ListSuggestZeroHitResponse, Response, [string, ListSuggestZeroHitVars] | undefined>(
    vars ? [SearchSuggestApiPath.LIST_ZERO_HIT, vars] : undefined
  )
}

// --- 人気キーワード（入力前の初期表示・自動分） ---

export const useSuggestPopularKeywordsGetApi = (
  vars: ListSuggestPopularKeywordsVars | undefined
) => {
  return useSWR<
    ListSuggestPopularKeywordsResponse,
    Response,
    [string, ListSuggestPopularKeywordsVars] | undefined
  >(vars ? [SearchSuggestApiPath.LIST_POPULAR_KEYWORDS, vars] : undefined)
}

// --- 成果レビュー ---

export const useSuggestAnalyticsGetApi = (vars: GetSuggestAnalyticsVars | undefined) => {
  return useSWR<GetSuggestAnalyticsResponse, Response, [string, GetSuggestAnalyticsVars] | undefined>(
    vars ? [SearchSuggestApiPath.GET_ANALYTICS, vars] : undefined
  )
}

export const useSuggestPreviewGetApi = (vars: GetSuggestPreviewVars | undefined) => {
  return useSWR<GetSuggestPreviewResponse, Response, [string, GetSuggestPreviewVars] | undefined>(
    vars ? [SearchSuggestApiPath.PREVIEW, vars] : undefined,
    // ライブプレビュー高速化: 取得中も直前結果を保持（空白ちらつき防止）＋同一内容は再取得しない
    { keepPreviousData: true, revalidateIfStale: false }
  )
}
